import { trpc } from '../../../../../trpc/react';
import type { RouterOutput } from '../../../../../trpc/common';
import { useFlags } from '../../../../context/featureFlag';
import { useViewer } from '../../../../context/viewer';
import { usePlanContext } from '../../../plan/context/PlanContext';

export type HubCalendarEvent =
  RouterOutput['calendarEvents']['list']['events'][number];

type HubEventTypeSummary = {
  id: string;
  name: string;
};

export function deriveHubEventTypesFromEvents(
  events: Array<{ event_type?: { id?: string; name?: string } | null }>,
): HubEventTypeSummary[] {
  const byId = new Map<string, HubEventTypeSummary>();
  for (const event of events) {
    const eventType = event.event_type;
    if (
      !eventType ||
      typeof eventType.id !== 'string' ||
      typeof eventType.name !== 'string'
    ) {
      continue;
    }
    if (!byId.has(eventType.id)) {
      byId.set(eventType.id, { id: eventType.id, name: eventType.name });
    }
  }
  return Array.from(byId.values()).sort((a, b) => a.name.localeCompare(b.name));
}

export function isHubEventsQueryEnabled(args: {
  teamId: string | null | undefined;
  startDate: string | null | undefined;
  endDate: string | null | undefined;
  hubConnected: boolean;
  hubEventsOnCalendarFlag: boolean;
}): boolean {
  return (
    !!args.teamId &&
    !!args.startDate &&
    !!args.endDate &&
    args.hubConnected &&
    args.hubEventsOnCalendarFlag
  );
}

export function useCalendarEvents({
  startDate,
  endDate,
}: {
  startDate: string | null;
  endDate: string | null;
}) {
  const { hubConnected } = useViewer();
  const { hubEventsOnCalendarFlag } = useFlags();
  const { teamId, selectedAthlete } = usePlanContext();

  const enabled = isHubEventsQueryEnabled({
    teamId,
    startDate,
    endDate,
    hubConnected,
    hubEventsOnCalendarFlag,
  });

  return trpc.calendarEvents.list.useQuery(
    {
      athleteId: selectedAthlete?.id ?? null,
      // enabled requires both dates; non-null assertions keep input aligned with
      // calendarEventsListInput's datetime({ offset: true }) schema.
      startDate: startDate!,
      endDate: endDate!,
    },
    {
      enabled,
    },
  );
}
